/**
 * RoleQuickSetup
 *
 * Inline "Quick Setup" panel for a single party role: from one base prefix,
 * atomically creates a full set of draft + final numbering series (Invoice,
 * Credit Note, and Debit Note for sales-direction roles) plus their GST-category
 * assignment rows. Used both inline in the Edit Role modal and in the Document
 * Numbering page's Assignments tab.
 */

import { useState } from 'react'

import { quickSetupRoleNumbering } from '../../services/document-numbering-service'
import { Button, Input, useToast } from '../ui'

interface RoleQuickSetupProps {
  orgId: string
  ledgerId: string
  partyRole: string
  onDone: () => void
  onCancel: () => void
}

function PrefixField({ prefix, isSaving, onChange }: { prefix: string; isSaving: boolean; onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex-1">
      <label className="block text-xs font-medium text-foreground-secondary el-mb-xs" htmlFor="quick-setup-prefix">
        Prefix
      </label>
      <Input
        hideMessage
        className="font-mono uppercase"
        disabled={isSaving}
        id="quick-setup-prefix"
        maxLength={6}
        placeholder="e.g., AB"
        value={prefix}
        onChange={e => onChange(e.target.value.toUpperCase())}
      />
    </div>
  )
}

export function RoleQuickSetup({ orgId, ledgerId, partyRole, onDone, onCancel }: RoleQuickSetupProps): JSX.Element {
  const { addToast } = useToast()
  const [prefix, setPrefix] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleApply(): Promise<void> {
    const trimmed = prefix.trim()
    if (!trimmed) { return }
    setIsSaving(true)
    setError(null)
    const result = await quickSetupRoleNumbering(orgId, ledgerId, { partyRole, prefix: trimmed })
    setIsSaving(false)
    if (!result.success) {
      setError(result.error ?? 'Failed to create numbering series')
      return
    }
    addToast({
      variant: 'success',
      message: `Created ${result.data?.series.length ?? 0} series and ${result.data?.assignmentsCreated ?? 0} assignments`,
    })
    onDone()
  }

  return (
    <div className="border border-border rounded-md el-p-md el-space-y-sm bg-muted">
      <p className="text-sm text-foreground-secondary">
        Creates draft and final numbering series for this role (Invoice, Credit Note, and Debit
        Note where applicable) plus their GST-category assignments — all from one prefix.
      </p>
      <div className="flex items-end el-gap-sm">
        <PrefixField isSaving={isSaving} prefix={prefix} onChange={setPrefix} />
        <Button disabled={isSaving || !prefix.trim()} variant="primary" onClick={() => { void handleApply() }}>
          {isSaving ? 'Creating…' : 'Create series & assignments'}
        </Button>
        <Button disabled={isSaving} variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
